import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SlidingIndicator from './SlidingIndicator';
import { Users, GitBranch, Building2, Database, Shield, ShieldCheck, Home, Receipt, SlidersHorizontal, PanelLeftClose, PanelLeft, Archive, Trash2, FileText, Folder, ChevronDown, Target, UserPlus, Globe, Lightbulb, Wrench, Mail, Send, Clock, IndianRupee, ShieldAlert, UserMinus, GraduationCap, Timer, HardHat } from 'lucide-react';

/** Accent per destination, drawn from the shared gradient tokens so the nav
 *  reads by colour as well as by label. */
const ACCENT = {
  '/': 'var(--g-primary)',
  '/corporate-hq': 'var(--g-data)',
  '/employees': 'var(--g-primary)',
  '/associates': 'var(--g-success)',
  '/salary-sheet': 'var(--g-important)',
  '/orgchart': 'var(--g-data)',
  '/dept-chart': 'var(--g-data)',
  '/kpi-center': 'var(--g-ai)',
  '/suggestions': 'var(--g-warning)',
  '/attrition': 'var(--g-alert)',
  '/training-plan': 'var(--g-success)',
  '/email': 'var(--g-celebrate)',
  '/pre-joining': 'var(--g-success)',
  '/ex-employees': 'linear-gradient(120deg,#94A3B8,#64748B)',
  '/requirement-tat': 'var(--g-celebrate)',
  '/letters': 'var(--g-data)',
  '/transfer-letter': 'var(--g-important)',
  '/settings': 'var(--g-important)',
  '/recycle-bin': 'var(--g-alert)',
  '/backup': 'var(--g-data)',
  '/rbac-settings': 'var(--g-ai)',
  '/rbac-audit': 'var(--g-ai)',
};
const DEFAULT_ACCENT = 'var(--g-primary)';

function NavLinkItem({ item, nested, collapsed, active, onNavigate }) {
  const Icon = item.icon;
  const accent = ACCENT[item.path] || DEFAULT_ACCENT;
  return (
    <Link key={item.path} to={item.path} title={collapsed ? item.label : ''} onClick={onNavigate}
      data-testid={`sidebar-${item.label.toLowerCase().replace(/\s/g, '-')}`}
      aria-current={active ? 'page' : undefined}
      className={`group relative flex items-center gap-3 px-3.5 py-2.5 rounded-[15px] text-[13.5px] font-medium
                  transition-colors duration-150 ease-smooth
                  ${collapsed ? 'justify-center' : ''} ${nested && !collapsed ? 'pl-4' : ''}
                  ${active ? 'text-white font-semibold'
                           : 'text-[#6B7280] hover:bg-[#F1F5FD]'}`}>
      <Icon className={`relative z-10 w-[18px] h-[18px] flex-shrink-0 transition-transform duration-[180ms]
                        ${active ? '' : 'group-hover:scale-[1.04]'}`}
            strokeWidth={active ? 2.15 : 1.7} />
      {!collapsed && <span className="relative z-10 truncate">{item.label}</span>}
    </Link>
  );
}

export default function Sidebar() {
  const { user, hasPerm } = useAuth();
  const location = useLocation();
  const [deskCollapsed, setDeskCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === '1');
  const [recordsOpen, setRecordsOpen] = useState(() => localStorage.getItem('recordsOpen') !== '0');

  // Below 1024px a full rail would eat most of the screen, so the sidebar
  // drops to an icon rail and expands as an overlay rather than squeezing
  // the page. The saved desktop preference is left untouched.
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches);
  const [narrowOpen, setNarrowOpen] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const onChange = (e) => { setIsNarrow(e.matches); if (!e.matches) setNarrowOpen(false); };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const collapsed = isNarrow ? !narrowOpen : deskCollapsed;
  const overlaying = isNarrow && narrowOpen;
  const toggle = () => {
    if (isNarrow) { setNarrowOpen((v) => !v); return; }
    const v = !deskCollapsed; setDeskCollapsed(v); localStorage.setItem('sidebarCollapsed', v ? '1' : '0');
  };
  // Tapping a destination on a phone should hand the screen back to the page.
  const closeOnNarrow = () => { if (isNarrow) setNarrowOpen(false); };
  const toggleRecords = () => { const v = !recordsOpen; setRecordsOpen(v); localStorage.setItem('recordsOpen', v ? '1' : '0'); };
  const isActive = (path) => location.pathname === path;

  const mainItems = [
    { icon: Home, label: 'Dashboard', path: '/', perm: true },
    { icon: Globe, label: 'Corporate HQ', path: '/corporate-hq', perm: hasPerm('employee.transfer') && (user?.role === 'admin' || user?.perm_scope === 'all') },
    { icon: Users, label: 'Employees', path: '/employees', perm: true },
    // Sits directly under Employees: staff and the shop floor are the two
    // halves of headcount, and HR moves between them constantly.
    { icon: HardHat, label: 'Associates', path: '/associates', perm: hasPerm('associate.view') },
    { icon: Receipt, label: 'Salary Sheet', path: '/salary-sheet', perm: hasPerm('salary.view') },
    { icon: GitBranch, label: 'Org Chart', path: '/orgchart', perm: true },
    { icon: Building2, label: 'Department Chart', path: '/dept-chart', perm: true },
    { icon: Target, label: 'KPI Center', path: '/kpi-center', perm: hasPerm('kpi.review') },
    { icon: Lightbulb, label: 'Suggestions', path: '/suggestions', perm: hasPerm('employee.view') },
    { icon: Clock, label: 'Late Coming', path: '/late-coming', perm: hasPerm('kpi.review') },
    { icon: UserMinus, label: 'Attrition', path: '/attrition', perm: hasPerm('kpi.review') },
    { icon: GraduationCap, label: 'Training Plan', path: '/training-plan', perm: hasPerm('kpi.review') },
    // Recruitment TAT, Customer Score and Customer Complaint were registers
    // somebody had to type into, and nobody ever did — 0 rows in all three.
    //
    //   Recruitment TAT is now DERIVED: a vacancy records the day it opened,
    //   the joiner records the day they started, and hiring links the two
    //   (`close_vacancy_on_joining`). Nothing to fill in, nothing to forget.
    //
    //   Customer Score and Customer Complaint move to another system and will
    //   arrive over the API bridge, like Operations and Maintenance do. Their
    //   KPIs stay in the master so their history and their place on 20 sheets
    //   survive the move; only the source changes when the bridge is built.
    // Vacancies and Requirement TAT are ONE screen: an open row on it is a
    // vacancy, a filled row is that same seat with its time-to-fill settled.
    // Two nav entries over one collection meant HR looked in two places to
    // answer one question.
    { icon: Timer, label: 'Vacancies & TAT', path: '/requirement-tat', perm: hasPerm('employee.view') },
    { icon: Wrench, label: 'Kaizen', path: '/kaizen', perm: hasPerm('employee.view') },
    { icon: Mail, label: 'Email', path: '/email', perm: hasPerm('email.view') },
  ];
  const recordsItems = [
    { icon: UserPlus, label: 'Pre-joining', path: '/pre-joining', perm: hasPerm('employee.create') },
    { icon: Archive, label: 'Ex-Employees', path: '/ex-employees', perm: true },
    { icon: IndianRupee, label: 'Expense', path: '/expense', perm: hasPerm('kpi.review') },
    { icon: ShieldAlert, label: 'Accident', path: '/accident', perm: hasPerm('kpi.review') },
    { icon: FileText, label: 'Letter History', path: '/letters', perm: hasPerm('letters.register') },
    { icon: Send, label: 'Transfer Letter', path: '/transfer-letter', perm: hasPerm('letters.generate') },
  ];
  const adminItems = [
    { icon: SlidersHorizontal, label: 'Settings', path: '/settings', perm: hasPerm('master.manage') },
    { icon: Trash2, label: 'Recycle Bin', path: '/recycle-bin', perm: hasPerm('employee.delete') },
    { icon: Database, label: 'Backup & Restore', path: '/backup', perm: hasPerm('backup.manage') },
    { icon: ShieldCheck, label: 'Roles & Permissions', path: '/rbac-settings', perm: hasPerm('rbac.manage') },
    { icon: Shield, label: 'RBAC Audit', path: '/rbac-audit', perm: hasPerm('rbac.manage') },
  ];

  const navRef = React.useRef(null);
  const visibleRecords = recordsItems.filter((i) => i.perm);
  const visibleAdmin = adminItems.filter((i) => i.perm);

  const renderItem = (item, nested) => (
    <NavLinkItem key={item.path} item={item} nested={nested} collapsed={collapsed}
                 active={isActive(item.path)} onNavigate={closeOnNarrow} />
  );

  return (
    <>
      {overlaying && (
        <div aria-hidden="true" onClick={() => setNarrowOpen(false)}
             className="fixed inset-0 z-40 bg-[#0F2A66]/30 backdrop-blur-[2px] lg:hidden" />
      )}
      <div data-testid="sidebar"
           className={`${collapsed ? 'w-[84px]' : 'w-[280px]'} ${overlaying ? 'fixed inset-y-0 left-0 z-50 shadow-[0_0_40px_rgba(15,23,42,0.14)]' : 'relative'}
                       enter-left flex-shrink-0 flex flex-col bg-white
                       transition-[width] duration-[180ms] ease-smooth`}>
        <div className="relative flex flex-col h-full">

        <div className="relative px-5 py-5 flex items-center gap-3">
          <div className="flex-shrink-0 grid place-items-center w-10 h-10 rounded-xl bg-white
                          shadow-[0_2px_10px_rgba(15,23,42,0.10)]">
            <img src="/assets/aampl-logo.jpg" alt="AAMPL" className="w-7 h-7 object-contain"
                 onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <h2 className="text-[16px] font-extrabold tracking-[-0.01em] text-[#0F172A] truncate">AAMPL HR</h2>
              <p className="text-[10px] font-semibold tracking-[0.16em] text-[#9CA3AF] uppercase truncate">
                {user?.role?.toUpperCase()}
              </p>
            </div>
          )}
          <button onClick={toggle} data-testid="sidebar-toggle"
                  title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                  aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                  className="group ml-auto grid place-items-center w-8 h-8 rounded-lg flex-shrink-0
                             text-[#9CA3AF] transition-colors duration-150
                             hover:text-[#2563EB] hover:bg-[#F1F5FD]">
            {collapsed
              ? <PanelLeft className="w-4 h-4 transition-transform duration-150 group-hover:rotate-[8deg]" />
              : <PanelLeftClose className="w-4 h-4 transition-transform duration-150 group-hover:-rotate-[8deg]" />}
          </button>
        </div>

        <nav ref={navRef} className="relative flex-1 px-3 pb-3 space-y-1 overflow-y-auto overflow-x-hidden">
          {/* Glides to the current page instead of the gradient being repainted
              on whichever link is active. Same component, same curve, as the
              tab strips. `aria-current="page"` is what marks the target, so
              the pill follows the router rather than any local state. */}
          <SlidingIndicator
            containerRef={navRef}
            activeSelector='[aria-current="page"]'
            axis="y"
            className="rounded-[15px] nav-active"
            deps={[collapsed, recordsOpen]} />
          {collapsed && (
            <button onClick={toggle} data-testid="sidebar-expand" title="Expand sidebar"
              aria-label="Expand sidebar" className="w-full flex items-center justify-center mb-2 p-2 rounded-xl bg-[#EFF6FF] hover:bg-[#DBEAFE] text-[#2563EB] transition-colors duration-150">
              <PanelLeft className="w-5 h-5" />
            </button>
          )}
          {mainItems.filter((i) => i.perm).map((item) => renderItem(item, false))}

          {visibleRecords.length > 0 && (
            collapsed ? (
              visibleRecords.map((item) => renderItem(item, false))
            ) : (
              <div className="pt-1.5">
                <button onClick={toggleRecords} data-testid="sidebar-records-group"
                  className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-[15px] text-[13.5px] font-medium text-[#6B7280] hover:bg-[#F1F5FD] transition-colors duration-150">
                  <Folder className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.7} />
                  Records
                  <ChevronDown className={`w-4 h-4 ml-auto transition-transform duration-300 ease-smooth ${recordsOpen ? '' : '-rotate-90'}`} />
                </button>
                {/* grid-rows animates open/closed without needing a fixed height. */}
                <div className={`grid transition-[grid-template-rows,opacity] duration-300 ease-smooth ${recordsOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                  <div className="overflow-hidden">
                    <div className="mt-1 ml-3 pl-2.5 border-l border-[rgba(37,99,235,0.14)] space-y-1">
                      {visibleRecords.map((item) => renderItem(item, true))}
                    </div>
                  </div>
                </div>
              </div>
            )
          )}

          {visibleAdmin.length > 0 && !collapsed && (
            <p className="px-3.5 pt-4 pb-1.5 text-[9.5px] font-bold uppercase tracking-[0.16em] text-[#9CA3AF]">Administration</p>
          )}
          {visibleAdmin.map((item) => renderItem(item, false))}
        </nav>

        {!collapsed && (
          <div className="relative p-3">
            <div className="rounded-2xl bg-[#F8F9FC] p-3">
              <div className="flex items-center gap-3">
                <span className="grid place-items-center w-10 h-10 rounded-full flex-shrink-0
                                 text-white text-xs font-bold"
                      style={{ background: 'var(--g-primary)' }} aria-hidden="true">
                  {(user?.full_name || '?').split(' ').map((x) => x[0]).slice(0, 2).join('').toUpperCase()}
                </span>
                <div className="min-w-0">
                  <p className="text-[13px] font-bold text-[#0F172A] truncate">{user?.full_name}</p>
                  <p className="text-[11px] text-[#6B7280] truncate">{user?.email}</p>
                </div>
              </div>
              <p className="mt-2 flex items-center gap-1.5 text-[11px] font-medium text-[#16A34A]">
                <span className="dot-live w-1.5 h-1.5 rounded-full bg-[#22C55E]" aria-hidden="true" />
                Online
              </p>
            </div>
          </div>
        )}
        </div>
      </div>
    </>
  );
}
